"use client";

import { useCallback, useMemo } from "react";
import {
  type DeleteOriginsRequest,
  type DeleteOriginsResponse,
  type Origin,
  type PostOriginRequest,
  type PutOriginRequest,
} from "@/generated/fastapi";
import { useAuth } from "@/state/globalStore";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback/toast";
import { deleteOrigins } from "../api/deleteOrigins";
import { getOrigins } from "../api/getOrigins";
import { postOrigin } from "../api/postOrigin";
import { putOrigin } from "../api/putOrigin";

export default function useOriginActions() {
  const { accessToken } = useAuth();

  const handleAddNewOriginClick = useCallback(
    async (
      request: PostOriginRequest,
      onSuccess?: (origin: Origin) => void,
    ): Promise<Origin | null> => {
      try {
        const result = await postOrigin(accessToken, request);
        if (result.status === "pending") {
          showWarningToast(result.message);
        } else {
          showSuccessToast(result.message);
        }
        onSuccess?.(result.origin);
        return result.origin;
      } catch {
        showErrorToast("Failed to add origin");
        return null;
      }
    },
    [accessToken],
  );

  const handleDeleteOriginsClick = useCallback(
    async (
      request: DeleteOriginsRequest,
      onSuccess?: () => void,
    ): Promise<DeleteOriginsResponse | null> => {
      try {
        const result = await deleteOrigins(accessToken, request);
        onSuccess?.();
        if (result.status === "pending" || result.status === "no_content") {
          showWarningToast(result.message);
        } else {
          showSuccessToast(result.message);
        }
        return result;
      } catch {
        showErrorToast("Failed to delete origins");
        return null;
      }
    },
    [accessToken],
  );

  const handleUpdateOriginClick = useCallback(
    async (
      request: PutOriginRequest,
      onSuccess?: (updatedOrigin: Origin) => Promise<Origin | null> | Origin,
    ): Promise<Origin | null> => {
      try {
        const result = await putOrigin(accessToken, request);
        await onSuccess?.(result.origin);
        if (result.status === "pending") {
          showWarningToast(result.message);
        } else {
          showSuccessToast(result.message);
        }
        return result.origin;
      } catch {
        showErrorToast("Failed to update origin");
        return null;
      }
    },
    [accessToken],
  );

  const handleCopyRawTextInputToClipboard = useCallback(
    async (originIds: string[]) => {
      try {
        const origins = await getOrigins(accessToken, {
          origin_ids: originIds,
        });

        if (origins.length === 0) {
          showErrorToast("Source material not found");
          return;
        }

        await navigator.clipboard.writeText(
          JSON.stringify(origins.map((origin) => origin.raw_text_input)),
        );
        showSuccessToast("Source material copied to clipboard");
      } catch {
        showErrorToast("Failed to retrieve source material");
      }
    },
    [accessToken],
  );

  return useMemo(
    () => ({
      handleAddNewOriginClick,
      handleDeleteOriginsClick,
      handleUpdateOriginClick,
      handleCopyRawTextInputToClipboard,
    }),
    [
      handleAddNewOriginClick,
      handleDeleteOriginsClick,
      handleUpdateOriginClick,
      handleCopyRawTextInputToClipboard,
    ],
  );
}
